import { opensheetTabUrl, SHEET_TABS } from "../config/spreadsheet";

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function rowValue(row, ...keys) {
  for (const key of keys) {
    if (row[key] != null && String(row[key]).trim() !== "") {
      return row[key];
    }
  }
  return "";
}

async function fetchSheetRows(tabName) {
  const res = await fetch(opensheetTabUrl(tabName), { cache: "no-store" });
  if (!res.ok) throw new Error(`Could not load ${tabName} sheet`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/** Email and order-id → customer name maps from Orders tab. */
export async function buildCustomerNameLookupsFromSheet() {
  const rows = await fetchSheetRows(SHEET_TABS.orders);
  const emailToName = {};
  const orderIdToName = {};

  for (const row of rows) {
    const name = String(rowValue(row, "Name", "name")).trim();
    if (!name) continue;
    const email = normalizeEmail(rowValue(row, "Email", "email"));
    const orderId = String(rowValue(row, "Order ID", "Order ID", "order id")).trim();
    if (email) emailToName[email] = name;
    if (orderId) orderIdToName[orderId] = name;
  }

  return { emailToName, orderIdToName };
}

export function resolveReviewerNameFromLookups(emailRaw, orderIdRaw, lookups) {
  const orderId = String(orderIdRaw || "").trim();
  if (orderId && lookups.orderIdToName[orderId]) {
    return lookups.orderIdToName[orderId];
  }
  const email = normalizeEmail(emailRaw);
  if (email && lookups.emailToName[email]) {
    return lookups.emailToName[email];
  }
  return "Customer";
}

function mapReviewRow(row, lookups) {
  const status = String(rowValue(row, "Status", "status")).trim().toLowerCase();
  if (status === "pending") return null;

  const comment = String(rowValue(row, "Comment", "comment")).trim();
  if (!comment) return null;

  const email = rowValue(row, "Email", "email");
  const orderId = rowValue(row, "Order ID", "Order ID", "order id");

  return {
    rating: Number(rowValue(row, "Rating", "rating")) || 0,
    comment,
    reviewer: resolveReviewerNameFromLookups(email, orderId, lookups),
    timestamp: String(rowValue(row, "Timestamp", "timestamp") || ""),
    productId: String(rowValue(row, "Product ID", "Product ID", "product id", "Box id")).trim(),
  };
}

/** Public read fallback when Apps Script is not yet redeployed. */
export async function fetchReviewsFromSheet({ productId = "", limit = 50 } = {}) {
  const [reviewRows, lookups] = await Promise.all([
    fetchSheetRows(SHEET_TABS.reviews),
    buildCustomerNameLookupsFromSheet(),
  ]);

  const wantPid = String(productId || "").trim();
  const max = Math.max(1, Math.min(limit, 50));

  const mapped = [];
  for (let i = reviewRows.length - 1; i >= 0; i -= 1) {
    const review = mapReviewRow(reviewRows[i], lookups);
    if (!review) continue;
    if (wantPid && review.productId !== wantPid) continue;
    mapped.push(review);
    if (mapped.length >= max) break;
  }

  return mapped;
}
